import React from 'react';
import PropTypes from 'prop-types';
import PageHeader from './PageHeader';

/**
 * PageLayout Component
 * 
 * Unified layout wrapper for all views.
 * Includes:
 * - Full-height container with dark mode support
 * - Optional PageHeader integration
 * - Content container with max-width and responsive padding
 * 
 * Usage:
 * <PageLayout 
 *   header={{ title: "Dashboard", subtitle: "Welcome" }}
 *   contentPadding="lg"
 * >
 *   {children}
 * </PageLayout>
 */
export const PageLayout = ({
  children,
  header = null,
  background = 'light', // 'light' = gray-50, 'dark' = gray-100
  contentPadding = 'md', // 'sm', 'md', 'lg', 'none'
  maxWidth = '7xl', // '7xl', 'full', 'none'
  className = ''
}) => {
  const bgClasses = {
    light: 'bg-gray-50 dark:bg-gray-900',
    dark: 'bg-gray-100 dark:bg-gray-900'
  };

  const paddingClasses = {
    none: '',
    sm: 'py-4',
    md: 'py-6',
    lg: 'py-8'
  };

  const maxWidthClasses = {
    '7xl': 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8',
    'full': 'w-full px-4 sm:px-6 lg:px-8',
    'none': ''
  };

  return (
    <div className={`min-h-screen ${bgClasses[background]} theme-transition ${className}`}>
      {/* Render PageHeader if header props provided */}
      {header && <PageHeader {...header} />}
      
      {/* Content container */}
      <div className={`${maxWidthClasses[maxWidth]} ${paddingClasses[contentPadding]}`}>
        {children}
      </div>
    </div>
  );
};

PageLayout.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.object, // PageHeader props
  background: PropTypes.oneOf(['light', 'dark']),
  contentPadding: PropTypes.oneOf(['none', 'sm', 'md', 'lg']),
  maxWidth: PropTypes.oneOf(['7xl', 'full', 'none']),
  className: PropTypes.string
};

export default PageLayout;
